import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert, FormField } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useRequestPasswordReset } from '../api/use-auth.mutation';
import { toUserMessage } from '@platform/api-client';
import {
  businessPasswordResetSchema,
  personalPasswordResetSchema,
  type RequestPasswordResetFormValues,
} from '../schemas/auth.schemas';
import { LOGIN_TAB_PARAM, type LoginTab } from '../utils/login-tab';
import { sanitizeOrgSlug } from '../utils/login-identifier';

/**
 * 비밀번호 재설정 요청 View.
 *
 * ── 왜 아이디를 함께 받는가 ─────────────────────────────────────
 * 이메일은 org 단위로만 유일하다(같은 사람이 개인 계정과 회사 계정을 각각 가질 수
 * 있어야 하므로). 그래서 이메일만으로는 어느 계정의 비밀번호를 되돌릴지 정할 수 없다.
 * 로그인과 같은 축(개인=아이디 / 기업=회사코드+아이디)으로 받아 한 계정으로 좁힌다.
 *
 * 보안 정책(백엔드): 계정 존재 여부와 관계없이 항상 동일한 성공 응답을 준다.
 * 프론트도 200 을 그대로 "발송되었습니다"로 보여주면 된다.
 */
export function RequestPasswordResetView() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<LoginTab>(() =>
    searchParams.get(LOGIN_TAB_PARAM) === 'business' ? 'business' : 'personal',
  );
  const [form, setForm] = useState<RequestPasswordResetFormValues>({
    email: '',
    orgSlug: '',
    loginId: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof RequestPasswordResetFormValues, string>>
  >({});

  const { mutate, isPending, isSuccess, error, reset } =
    useRequestPasswordReset();

  const isBusiness = tab === 'business';

  const onChange =
    (key: keyof RequestPasswordResetFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        key === 'orgSlug' ? sanitizeOrgSlug(e.target.value) : e.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
      if (fieldErrors[key])
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
      if (error) reset();
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const schema = isBusiness
      ? businessPasswordResetSchema
      : personalPasswordResetSchema;
    const parsed = schema.safeParse({
      email: form.email,
      loginId: form.loginId,
      ...(isBusiness ? { orgSlug: form.orgSlug } : {}),
    });
    if (!parsed.success) {
      // 개인 스키마에는 orgSlug 가 없어 flatten 결과 타입이 좁혀진다 — 화면은 세 칸을
      // 한 모양으로 다루므로 여기서 넓혀 받는다.
      const flat = parsed.error.flatten().fieldErrors as Partial<
        Record<keyof RequestPasswordResetFormValues, string[]>
      >;
      setFieldErrors({
        email: flat.email?.[0],
        orgSlug: flat.orgSlug?.[0],
        loginId: flat.loginId?.[0],
      });
      return;
    }
    setFieldErrors({});
    mutate({
      email: form.email.trim(),
      loginId: form.loginId.trim().toLowerCase(),
      ...(isBusiness ? { orgSlug: form.orgSlug.trim().toLowerCase() } : {}),
    });
  };

  if (isSuccess) {
    return (
      <div className="mx-auto w-full max-w-md py-10 text-center">
        <h1 className="mb-4 text-2xl font-bold text-adobe-dark dark:text-neutral-100">
          메일을 확인해주세요
        </h1>
        <p className="text-adobe-muted dark:text-neutral-400">
          입력하신 정보와 일치하는 계정이 있다면 재설정 링크가 발송됩니다.
        </p>
        <p className="mt-2 text-xs text-adobe-muted dark:text-neutral-500">
          메일이 오지 않으면 스팸함을 확인해주세요.
        </p>
        <Link
          to={PATHS.LOGIN}
          className="mt-6 inline-block text-primary hover:underline"
        >
          로그인 페이지로 이동
        </Link>
      </div>
    );
  }

  const tabButton = (value: LoginTab, label: string) => (
    <button
      key={value}
      type="button"
      role="tab"
      aria-selected={tab === value}
      onClick={() => {
        setTab(value);
        setFieldErrors({});
      }}
      className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
        tab === value
          ? 'bg-brand-500 text-white'
          : 'text-adobe-muted hover:text-adobe-dark dark:text-neutral-400 dark:hover:text-neutral-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-sm py-10">
      <h1 className="mb-6 text-2xl font-bold text-adobe-dark dark:text-neutral-100">
        비밀번호 찾기
      </h1>

      <div
        role="tablist"
        className="mb-6 flex gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800"
      >
        {tabButton('personal', '개인 회원')}
        {tabButton('business', '기업 회원')}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <FormField
          label="아이디"
          type="text"
          name="loginId"
          autoComplete="username"
          placeholder=""
          required
          value={form.loginId}
          onChange={onChange('loginId')}
          error={fieldErrors.loginId}
        />

        {isBusiness && (
          <FormField
            label="회사코드"
            type="text"
            name="orgSlug"
            autoComplete="organization"
            placeholder="howcost"
            prefix="@"
            required
            value={form.orgSlug}
            onChange={onChange('orgSlug')}
            error={fieldErrors.orgSlug}
            hint="아이디 뒤에 붙는 회사 식별자입니다."
          />
        )}

        <FormField
          label="이메일"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={onChange('email')}
          error={fieldErrors.email}
          hint="가입 시 사용한 이메일로 재설정 링크를 발송합니다."
        />

        {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

        <button
          type="submit"
          disabled={isPending}
          className="w-full rounded bg-adobe-red py-2 font-bold text-white disabled:opacity-50"
        >
          {isPending ? '요청 중...' : '재설정 링크 받기'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-adobe-muted dark:text-neutral-400">
        아이디가 기억나지 않나요?{' '}
        <Link to={PATHS.FIND_LOGIN_ID} className="text-primary hover:underline">
          아이디 찾기
        </Link>
      </p>
      <p className="mt-2 text-center text-sm text-adobe-muted dark:text-neutral-400">
        <Link
          to={PATHS.LOGIN}
          className="hover:text-adobe-dark dark:hover:text-neutral-100"
        >
          로그인으로 돌아가기
        </Link>
      </p>
    </div>
  );
}
